// Dragging an entry onto a hot bar box (qa-runs#369, slice B): the sheet's saves, skills and abilities in the dice bag,
// its attacks there and in Properties' Actions, and the dice bag's quick dice carry what a box would hold, by reference
// (`attack:<key>`, `save:<key>`, `dice:1d20`…), never a number. "+" on a box stays the tap way to fill it.
import type { DragEvent } from 'react';
import { isHotRef } from '@dragons-table/vtt-shared';

/** The drag's own type: a page's text drags and files are not boxes. */
export const HOT_MIME = 'application/x-dragons-table-hotbox';

/** What a drag carries: the token whose sheet it came from (none for plain dice), its reference and its name. */
export interface HotDrag { token: string | null; ref: string; label: string }

/** Props that make a control a drag source for a box. Its click still does what it did. */
export function hotDragProps(drag: HotDrag): { draggable: true; onDragStart: (e: DragEvent) => void; 'data-hot-ref': string } {
  return {
    draggable: true,
    'data-hot-ref': drag.ref,
    onDragStart: (e: DragEvent) => {
      if (!e.dataTransfer) return;
      e.dataTransfer.setData(HOT_MIME, JSON.stringify(drag));
      e.dataTransfer.setData('text/plain', drag.label);
      e.dataTransfer.effectAllowed = 'copy';
    },
  };
}

/** Does this drag carry a box entry (readable while dragging over, when only the types are)? */
export const carriesHot = (e: DragEvent): boolean => !!e.dataTransfer && Array.from(e.dataTransfer.types ?? []).includes(HOT_MIME);

/** The entry a drop carries, checked; null for anything else. */
export function readHotDrop(e: DragEvent): HotDrag | null {
  try {
    const v = JSON.parse(e.dataTransfer?.getData(HOT_MIME) || 'null') as Partial<HotDrag> | null;
    if (!v || !isHotRef(v.ref) || typeof v.label !== 'string') return null;
    return { token: typeof v.token === 'string' ? v.token : null, ref: v.ref, label: v.label.slice(0, 80) };
  } catch {
    return null;
  }
}
